/**
 * ErrorBoundary — catches uncaught render and lifecycle errors so the app
 * shows a recovery UI instead of a blank document.
 *
 * Without this, an uncaught error during render unmounts the entire React
 * tree, leaving the DOM empty (a "black screen" with only Vite's body
 * background visible). With this, we get:
 *   - A visible error panel describing what happened
 *   - Buttons to reset back to the menu or try the saved run again
 *   - The full error message + stack in the console for diagnostics
 *
 * Common cases this saves us from in Geo-Sleeper:
 *   - Pending maplibre callbacks firing on a torn-down map after a screen
 *     transition (e.g. round end → kill-summary while a map source is
 *     still updating async)
 *   - Stale closure references that React 18's strict-mode double-invoke
 *     can occasionally surface
 *   - Any future bug that throws synchronously during render
 */

import React from 'react';
import { theme } from '../styles/theme';

interface ErrorBoundaryState {
  error: Error | null;
  resetCount: number;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null, resetCount: 0 };

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] Render error caught:', error);
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] Component stack:', errorInfo.componentStack);
  }

  handleReset = () => {
    // Force a remount of the children by bumping a key. Existing app state
    // (the App's useState) is preserved across the unmount/remount because
    // React's reconciliation key keeps the same component identity. But
    // any stuck/torn-down resources (like a removed maplibre map kept
    // around in a ref) get a clean rebuild.
    this.setState(prev => ({ error: null, resetCount: prev.resetCount + 1 }));
  };

  handleHardReload = () => {
    // Last resort — full page reload. Persisted run state in localStorage
    // is unaffected, so the player can hit Continue Run from the menu and
    // resume wherever pendingReward / midRoundSave left them.
    window.location.reload();
  };

  render() {
    if (this.state.error) {
      const message = this.state.error.message || String(this.state.error);
      return (
        <div style={styles.container}>
          <div style={styles.title}>SOMETHING BROKE</div>
          <div style={styles.subtitle}>
            The game hit an unrecoverable error during rendering. Your run
            is still saved — pick one of the recovery options below.
          </div>
          <div style={styles.errorBox}>
            <div style={styles.errorLabel}>ERROR</div>
            <div style={styles.errorMessage}>{message}</div>
          </div>
          <div style={styles.buttonRow}>
            <button style={styles.button} onClick={this.handleReset}>
              TRY AGAIN
            </button>
            <button style={{ ...styles.button, ...styles.buttonReload }} onClick={this.handleHardReload}>
              RELOAD PAGE
            </button>
          </div>
          <div style={styles.hint}>
            Check the browser console (F12) for the full stack trace.
          </div>
        </div>
      );
    }
    // Bumping `key` on reset forces children to remount cleanly.
    return <React.Fragment key={this.state.resetCount}>{this.props.children}</React.Fragment>;
  }
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(8, 12, 20, 0.98)',
    color: theme.colors.textPrimary,
    fontFamily: theme.fonts.mono,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
    padding: '40px',
    zIndex: 999999,
  },
  title: {
    fontSize: '20px',
    fontWeight: 700,
    letterSpacing: '6px',
    color: '#ff8866',
  },
  subtitle: {
    fontSize: '12px',
    color: theme.colors.textSecondary,
    maxWidth: '520px',
    textAlign: 'center',
    lineHeight: 1.6,
  },
  errorBox: {
    background: 'rgba(255, 80, 80, 0.06)',
    border: '1px solid rgba(255, 80, 80, 0.3)',
    borderRadius: '6px',
    padding: '14px 18px',
    maxWidth: '600px',
    width: '100%',
    marginTop: '12px',
  },
  errorLabel: {
    fontSize: '9px',
    letterSpacing: '2px',
    color: '#ff5050',
    fontWeight: 700,
    marginBottom: '6px',
  },
  errorMessage: {
    fontSize: '11px',
    color: '#ffaaaa',
    fontFamily: theme.fonts.mono,
    wordBreak: 'break-word',
    lineHeight: 1.5,
  },
  buttonRow: {
    display: 'flex',
    gap: '12px',
    marginTop: '16px',
  },
  button: {
    background: 'rgba(68, 204, 136, 0.08)',
    border: '1px solid rgba(68, 204, 136, 0.5)',
    color: '#44cc88',
    padding: '10px 24px',
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '2px',
    cursor: 'pointer',
    fontFamily: theme.fonts.mono,
    borderRadius: '4px',
  },
  buttonReload: {
    background: 'rgba(255, 80, 80, 0.06)',
    borderColor: 'rgba(255, 80, 80, 0.5)',
    color: '#ff8888',
  },
  hint: {
    fontSize: '10px',
    color: theme.colors.textDim,
    letterSpacing: '1px',
    marginTop: '8px',
  },
};
